import { ClientType, GFAShareholder } from "@dune/types";
import {
	Button,
	ControlledInput,
	ControlledRadioGroup,
	ControlledSelect,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	Input,
} from "@dune/ui";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { FormProvider, SubmitHandler, useFormContext } from "react-hook-form";
import SirenSocietyForm from "../brokers/constructionSites/SirenClientForm";
import {
	GFAShareholderFormData,
	useGFAShareholderForm,
} from "./useGFAShareholdersAndExperiencesForm";

interface GFAShareholdersContainerProps {
	isForm?: boolean;
}

const GFAShareholdersContainer = ({
	isForm = true,
}: GFAShareholdersContainerProps) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { setValue, watch, trigger } = useFormContext();
	const shareholders = watch("shareholders");

	return (
		<div className="flex flex-col gap-4">
			{isForm && (
				<div className="flex flex-row gap-4">
					<h1 className="text-[#144C65] text-lg font-medium">ACTIONNAIRES</h1>
				</div>
			)}
			<div className="border rounded-lg p-4 flex flex-col gap-4">
				<div className="flex flex-col gap-4">
					<div className="flex flex-row gap-4 w-[95%]">
						<div className="w-1/4">Associé</div>
						<div className="w-1/4">Direct / Indirect</div>
						<div className="w-1/4">% de participation</div>
						<div className="w-1/4">Fonction dans l'entreprise</div>
					</div>
					{shareholders?.map((shareholder: GFAShareholder, index: number) => (
						<div
							key={shareholder.id}
							className="flex flex-row gap-4 items-center"
						>
							<div className="flex flex-row gap-4 w-[95%]">
								<div className="w-1/4">
									<Input
										readOnly
										value={
											shareholder.shareholderType === "USER"
												? `${shareholder.shareholderUser?.firstname} ${shareholder.shareholderUser?.lastname}`
												: `${shareholder.shareholderSociety?.denomination}`
										}
									/>
								</div>

								<div className="w-1/4">
									<Input readOnly value={shareholder.type ?? ""} />
								</div>

								<div className="w-1/4">
									<Input
										readOnly
										value={shareholder.participationPercentage ?? ""}
									/>
								</div>

								<div className="w-1/4">
									<Input
										readOnly
										value={shareholder.positionInTheCompany ?? ""}
									/>
								</div>
							</div>
							{isForm && (
								<Trash2
									onClick={() => {
										setValue("shareholders", [
											...shareholders.slice(0, index),
											...shareholders.slice(index + 1),
										]);
										trigger();
									}}
								/>
							)}
						</div>
					))}
					{isForm && (
						<Button
							onClick={() => {
								setIsModalOpen(true);
							}}
							variant="none"
							size="none"
							className="self-start"
							type="button"
						>
							<div className="flex flex-row items-center gap-2 font-medium text-sm text-primary">
								<Plus /> Ajouter
							</div>
						</Button>
					)}
				</div>
			</div>
			{isModalOpen && (
				<GFAAddShareholderModal
					isOpen={isModalOpen}
					onClose={() => setIsModalOpen(false)}
				/>
			)}
		</div>
	);
};

type GFAAddShareholderModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

const GFAAddShareholderModal = ({
	isOpen,
	onClose,
}: GFAAddShareholderModalProps) => {
	const { setValue, watch } = useFormContext();
	const shareholders = watch("shareholders");
	const { form: shareholderForm } = useGFAShareholderForm();

	const onSubmit: SubmitHandler<GFAShareholderFormData> = (data) => {
		setValue("shareholders", [
			...shareholders,
			{ id: shareholders.length + 1, ...data },
		]);
		onClose();
	};
	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="flex flex-col">
				<FormProvider {...shareholderForm}>
					<div className="flex flex-col h-full">
						<DialogHeader>
							<h1 className="text-[#144C65] text-2xl font-medium">
								Ajouter un actionnaire
							</h1>
						</DialogHeader>
						<form
							id="shareholder-form"
							onSubmit={shareholderForm.handleSubmit(onSubmit)}
							className="flex-1 overflow-y-auto p-1"
						>
							<GFAShareholderForm />
						</form>
						<DialogFooter className="mt-4">
							<Button
								type="button"
								className="flex-1"
								variant="outline"
								onClick={onClose}
							>
								Annuler
							</Button>
							<Button
								type="button"
								className="flex-1"
								onClick={() => {
									shareholderForm.handleSubmit(onSubmit)();
								}}
							>
								Ajouter
							</Button>
						</DialogFooter>
					</div>
				</FormProvider>
			</DialogContent>
		</Dialog>
	);
};

const GFAShareholderForm = () => {
	const {
		control: shareholderControl,
		watch: shareholderWatch,
		formState: { errors },
	} = useFormContext<GFAShareholderFormData>();
	const shareholderType = shareholderWatch("shareholderType");
	return (
		<div className="flex flex-col gap-4">
			<ControlledRadioGroup
				control={shareholderControl}
				name="shareholderType"
				label="Personne physique ou morale ?"
				items={[
					{
						id: "user-radio",
						value: "USER" satisfies ClientType,
						label: "Physique",
					},
					{
						id: "society-radio",
						value: "SOCIETY" satisfies ClientType,
						label: "Morale",
					},
				]}
			/>
			<div className="flex flex-col border-solid border-1 border-gray-200 rounded-lg p-4 gap-4">
				{shareholderType === "USER" ? (
					<div className="flex flex-col items-center gap-4">
						<div className="flex flex-row gap-4 w-full">
							<ControlledInput
								control={shareholderControl}
								name={"shareholderUser.lastname"}
								label="Nom"
								error={
									"shareholderUser" in errors
										? errors.shareholderUser?.lastname?.message?.toString()
										: undefined
								}
							/>
							<ControlledInput
								control={shareholderControl}
								name={"shareholderUser.firstname"}
								label="Prénom"
								error={
									"shareholderUser" in errors
										? errors.shareholderUser?.firstname?.message?.toString()
										: undefined
								}
							/>
						</div>
					</div>
				) : (
					<div className="flex flex-col justify-start items-center gap-4">
						<SirenSocietyForm formKey="shareholderSociety" />
					</div>
				)}
			</div>
			<ControlledSelect
				control={shareholderControl}
				name={"type"}
				label="Direct / Indirect"
				placeholder="Direct / Indirect"
				options={[
					{ label: "Direct", value: "DIRECT" },
					{ label: "Indirect", value: "INDIRECT" },
				]}
			/>
			<ControlledInput
				control={shareholderControl}
				name={"participationPercentage"}
				label="% de participation"
				placeholder="% de participation"
				type="number"
				error={errors.participationPercentage?.message?.toString()}
			/>
			<ControlledInput
				control={shareholderControl}
				name={"positionInTheCompany"}
				label="Fonction dans l'entreprise"
				placeholder="Fonction dans l'entreprise"
				error={errors.positionInTheCompany?.message?.toString()}
			/>
		</div>
	);
};

export default GFAShareholdersContainer;
